/**
 * CertificationSection
 * 
 * Search result section for Process Owner Certification (EX7)
 * Displays matching certifications and documents grouped by process owner
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tooltip, Collapse } from 'antd';
import { 
  FilePdfOutlined, 
  FileExcelOutlined, 
  FileWordOutlined, 
  FilePptOutlined,
  FileImageOutlined,
  FileUnknownOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { 
  ProcessOwner, 
  ProcessCertification, 
  CertificationDocument 
} from '../../../../services/certificationService';
import HighlightText from '../components/HighlightText';

// ========================================
// Styled Components
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #FDF2F8;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #FBCFE8;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #9D174D;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const StyledCollapse = styled(Collapse)`
  background: white;
  border: none;
  border-radius: 0;

  .ant-collapse-item {
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
    background: #F8FAFC;
    align-items: center !important;
  }

  .ant-collapse-content {
    border-top: 1px solid #E5E7EB;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

const ProcessOwnerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ProcessOwnerAvatar = styled.span`
  width: 28px;
  min-width: 28px;
  height: 28px;
  min-height: 28px;
  border-radius: 50%;
  background: #EC4899;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
`;

const ProcessOwnerName = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #1F2937;
`;

const ProcessOwnerStats = styled.span`
  font-family: Inter;
  font-size: 12px;
  color: #6B7280;
  margin-left: auto;
`;

const SubSectionHeader = styled.div`
  background: #FAFAFA;
  padding: 8px 16px;
  border-bottom: 1px solid #E5E7EB;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 11px;
  color: #6B7280;
  background: #E5E7EB;
  padding: 2px 6px;
  border-radius: 8px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    white-space: nowrap;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 10px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: middle;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

// Chip Components
const ProcessLabelChip = styled.span`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  background: #EEF2FF;
  color: #3730A3;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  
  &:hover {
    opacity: 0.8;
  }
`;

const QuarterStatusChip = styled.span<{ $completed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  background: ${props => props.$completed ? '#DCFCE7' : 'transparent'};
  color: ${props => props.$completed ? '#16A34A' : '#1F2937'};
`;

const FileNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FileIcon = styled.span<{ $type: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  font-size: 12px;
  
  ${props => {
    switch (props.$type) {
      case 'pdf':
        return `
          background: #FEE2E2;
          color: #DC2626;
        `;
      case 'xls':
      case 'xlsx':
        return `
          background: #DCFCE7;
          color: #16A34A;
        `;
      case 'doc':
      case 'docx':
        return `
          background: #DBEAFE;
          color: #2563EB;
        `;
      case 'ppt':
      case 'pptx':
        return `
          background: #FFEDD5;
          color: #EA580C;
        `;
      case 'png':
      case 'jpg':
        return `
          background: #E0E7FF;
          color: #4F46E5;
        `;
      default:
        return `
          background: #F3F4F6;
          color: #6B7280;
        `;
    }
  }}
`;

const FileName = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #1F2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
`;

const FileSizeText = styled.span`
  font-size: 11px;
  color: #6B7280;
`;

const DateText = styled.span`
  font-size: 11px;
  color: #6B7280;
`;


// ========================================
// Types
// ========================================

export interface ProcessOwnerCertificationData {
  processOwner: ProcessOwner;
  certifications: ProcessCertification[];
  documents: CertificationDocument[];
}

export interface CertificationResult {
  processOwners: ProcessOwnerCertificationData[];
}

export interface CertificationSectionProps {
  data: CertificationResult;
  searchText: string;
}

// ========================================
// Helper Functions
// ========================================

const getFileIcon = (fileType: CertificationDocument['fileType']) => {
  switch (fileType) {
    case 'pdf':
      return <FilePdfOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    case 'ppt':
    case 'pptx':
      return <FilePptOutlined />;
    case 'png':
    case 'jpg':
      return <FileImageOutlined />;
    default:
      return <FileUnknownOutlined />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  });
};

// ========================================
// Component
// ========================================

const CertificationSection: React.FC<CertificationSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToCertification = (processOwnerId: string) => {
    navigate('/certification', { state: { selectedProcessOwnerId: processOwnerId } });
  };

  // Certification columns
  const certificationColumns: ColumnsType<ProcessCertification> = [
    {
      title: 'Process',
      dataIndex: 'processLabel',
      key: 'processLabel',
      width: 200,
      render: (label: string, record) => (
        <ProcessLabelChip onClick={() => handleNavigateToCertification(record.processOwnerId)}>
          <HighlightText text={label || '-'} searchText={searchText} />
        </ProcessLabelChip>
      ),
    },
    {
      title: 'Q1',
      dataIndex: 'q1SignOffDate',
      key: 'q1SignOffDate',
      width: 120,
      align: 'center',
      render: (date: string | null) => (
        <QuarterStatusChip $completed={!!date}>
          {date ? formatDate(date) : '-'}
        </QuarterStatusChip>
      ),
    },
    {
      title: 'Q2',
      dataIndex: 'q2SignOffDate',
      key: 'q2SignOffDate',
      width: 120,
      align: 'center',
      render: (date: string | null) => (
        <QuarterStatusChip $completed={!!date}>
          {date ? formatDate(date) : '-'}
        </QuarterStatusChip>
      ),
    },
    {
      title: 'Q3',
      dataIndex: 'q3SignOffDate',
      key: 'q3SignOffDate',
      width: 120,
      align: 'center',
      render: (date: string | null) => (
        <QuarterStatusChip $completed={!!date}>
          {date ? formatDate(date) : '-'}
        </QuarterStatusChip>
      ),
    },
    {
      title: 'Q4',
      dataIndex: 'q4SignOffDate',
      key: 'q4SignOffDate',
      width: 120,
      align: 'center',
      render: (date: string | null) => (
        <QuarterStatusChip $completed={!!date}>
          {date ? formatDate(date) : '-'}
        </QuarterStatusChip>
      ),
    },
  ];

  // Documents columns
  const documentsColumns: ColumnsType<CertificationDocument> = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 300,
      render: (fileName: string, record) => (
        <FileNameContainer>
          <FileIcon $type={record.fileType}>
            {getFileIcon(record.fileType)}
          </FileIcon>
          <Tooltip title={fileName}>
            <FileName>
              <HighlightText text={fileName} searchText={searchText} />
            </FileName>
          </Tooltip>
        </FileNameContainer>
      ),
    },
    {
      title: 'Size',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 80,
      render: (size: number) => (
        <FileSizeText>{formatFileSize(size)}</FileSizeText>
      ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (date: string) => (
        <DateText>{formatDate(date)}</DateText>
      ),
    },
  ];

  // Calculate total results
  const totalResults = data.processOwners.reduce((acc, po) => 
    acc + po.certifications.length + po.documents.length, 0
  );

  // Don't render if no results
  if (totalResults === 0) {
    return null;
  }

  // Build collapse items
  const collapseItems = data.processOwners
    .filter(po => po.certifications.length > 0 || po.documents.length > 0)
    .map(po => ({
      key: po.processOwner.id,
      label: (
        <ProcessOwnerHeader>
          <ProcessOwnerAvatar>
            {po.processOwner.processOwnerName.charAt(0).toUpperCase()}
          </ProcessOwnerAvatar>
          <ProcessOwnerName>
            <HighlightText text={po.processOwner.processOwnerName} searchText={searchText} />
          </ProcessOwnerName>
          <ProcessOwnerStats>
            {po.certifications.length} processes, {po.documents.length} documents
          </ProcessOwnerStats>
        </ProcessOwnerHeader>
      ),
      children: (
        <>
          {/* Certifications Sub-section */}
          {po.certifications.length > 0 && (
            <>
              <SubSectionHeader>
                <SubSectionTitle>Process Certifications</SubSectionTitle>
                <SubSectionCount>{po.certifications.length}</SubSectionCount>
              </SubSectionHeader>
              <TableWrapper>
                <Table
                  columns={certificationColumns}
                  dataSource={po.certifications}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </TableWrapper>
            </>
          )}

          {/* Documents Sub-section */}
          {po.documents.length > 0 && (
            <>
              <SubSectionHeader>
                <SubSectionTitle>Documents</SubSectionTitle>
                <SubSectionCount>{po.documents.length}</SubSectionCount>
              </SubSectionHeader>
              <TableWrapper>
                <Table
                  columns={documentsColumns}
                  dataSource={po.documents}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </TableWrapper>
            </>
          )}

        </>
      ),
    }));

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>
          Process Owner Certification ({totalResults} results)
        </SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <StyledCollapse
          items={collapseItems}
          defaultActiveKey={collapseItems.map(item => item.key)}
          expandIconPosition="start"
        />
      </ContentContainer>
    </SectionContainer>
  );
};

export default CertificationSection;
